// Result-view "Offset" display aid.
//
// When the backend cuts a base-grid edge, two nodes end up on the exact same
// coordinate, so in the map they render on top of each other and the cut is
// invisible. This helper returns a *transformed copy* of the elements where
// each coincident dead-end node (one with exactly one connected edge) is
// nudged a small fixed screen distance along that single edge (toward its far
// endpoint), opening up the gap along the pipe run so the two nodes become
// visible. Coincident nodes with several edges stay put.
//
// It is purely visual: only coincident nodes get new (moved) position objects;
// every other element is returned by reference. Edges carry no geometry of
// their own (Edge.jsx resolves endpoints by node id from this same array), so
// the connected pipes follow the moved nodes automatically.
//
// The offset is computed in screen/pixel space (like findEdgeToSplit in
// splitEdge.js), so the visual gap stays constant across zoom levels.

// Two positions within this many degrees (~1 cm) count as the same coordinate.
const COINCIDENT_EPS = 1e-7;

export function offsetCoincidentNodes(elements, map, offsetPx = 8) {
  if (!Array.isArray(elements) || !map) return elements;

  // Group nodes by rounded position to find coincident stacks.
  const groups = new Map();
  const posById = new Map();
  for (const el of elements) {
    if (el.type !== "node" || !Array.isArray(el.position)) continue;
    posById.set(el.id, el.position);
    const key =
      Math.round(el.position[0] / COINCIDENT_EPS) +
      ":" +
      Math.round(el.position[1] / COINCIDENT_EPS);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(el.id);
  }

  // Ids of nodes that share a coordinate with at least one other node.
  const toMove = new Set();
  for (const ids of groups.values()) {
    if (ids.length >= 2) for (const id of ids) toMove.add(id);
  }
  if (toMove.size === 0) return elements;

  // For each movable node, collect the positions of its edge neighbors so we
  // know which direction "its own edge" points.
  const neighborsById = new Map();
  const addNeighbor = (id, neighborPos) => {
    if (!toMove.has(id) || !neighborPos) return;
    if (!neighborsById.has(id)) neighborsById.set(id, []);
    neighborsById.get(id).push(neighborPos);
  };
  for (const el of elements) {
    if (el.type !== "edge") continue;
    addNeighbor(el.startNodeId, posById.get(el.endNodeId));
    addNeighbor(el.endNodeId, posById.get(el.startNodeId));
  }

  return elements.map((el) => {
    if (el.type !== "node" || !toMove.has(el.id)) return el;
    const neighbors = neighborsById.get(el.id);
    // Only move dead-end nodes: a node is offset solely when it has exactly one
    // connected edge, and it moves along that single edge. Nodes with several
    // edges stay put.
    if (!neighbors || neighbors.length !== 1) return el;

    const origin = map.latLngToContainerPoint({
      lat: el.position[0],
      lng: el.position[1],
    });

    // Unit direction toward the single neighbor (pixel space).
    const nb = neighbors[0];
    const p = map.latLngToContainerPoint({ lat: nb[0], lng: nb[1] });
    const vx = p.x - origin.x;
    const vy = p.y - origin.y;
    const len = Math.hypot(vx, vy);
    if (len < 1e-6) return el; // neighbor coincides too: no direction

    const moved = map.containerPointToLatLng({
      x: origin.x + (vx / len) * offsetPx,
      y: origin.y + (vy / len) * offsetPx,
    });
    return { ...el, position: [moved.lat, moved.lng] };
  });
}
